import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CommonSearchBar from '../../../Components/CommonSearchBar';
import CommonPagination from '../../../Components/CommonPagination';
import CommonLoader from '../../../Components/CommonLoader';
import CommonTable from '../../../Components/CommonTable';
import StatusBadge from '../../../Components/StatusBadge';
import NoDataPage from '../../../Components/NoDataPage';
import PageHeading from '../../../Components/PageHeading';
import { useToast } from '../../../Components/CommonToast';
import RiskDetail from './RiskDetail';
import { risk_portal_risks } from '../../../services';
import { fontSize, Body } from '../../../Constants/Fonts';
import { useRbac } from '../rbac';
import {
    currentUserEmail,
    visibleToMe,
    resolveCurrentStage,
    stageOwnerRoleFor,
    canActOnRisk,
} from '../workflow';

// ═══════════════════════════════════════════════════════════════
// My Risks — the signed-in user's personal risk queue.
// ---------------------------------------------------------------
// Lists every risk the user is involved in (via visibleToMe: named owner OR
// their role owns the current stage). For each, the Status column tells them
// whether it's their turn ("Action required") or it's sitting with another
// role/person ("Awaiting response" — role + owner name), following the same
// stage-driven flow as the RiskDetail screen. UX only — the backend enforces
// visibility + actions independently.
// ═══════════════════════════════════════════════════════════════

const extractItem = (res) =>
    res?.data?.response_data?.data || res?.data?.data ||
    res?.statusCode?.response_data?.data || res?.statusCode?.data || null;

const titleCase = (s) => String(s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const PAGE_SIZE = 25;

export default function MyRisks({ onOpenRisk }) {
    const { role, stages } = useRbac();
    const email = useMemo(() => currentUserEmail(), []);

    const [all, setAll] = useState([]);        // every risk visible to me (unpaged)
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);        // 0-based (CommonPagination)
    const [searchText, setSearchText] = useState('');
    const [search, setSearch] = useState('');
    const [detailRiskId, setDetailRiskId] = useState(null);

    const { notify, toastNode } = useToast();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            // Fetch a broad set and scope client-side (same approach the Risk
            // Register uses for stage-scoped roles), so pagination + status are
            // computed against the full "mine" set.
            //
            // Pass `owner=<email>` so the backend returns the risks this user
            // owns — mirroring the reference portal's my_risks call
            // (RiskPortal.js `loadRisks`). Without it, the generic list is scoped
            // to what the caller's ROLE can broadly see, and a risk_owner (who
            // has no org-wide visibility) gets an empty set — so risks assigned
            // to them never appear here. The client-side visibleToMe filter below
            // still applies as defense-in-depth.
            const params = { page: 1, page_size: 500 };
            if (email) params.owner = email;
            const d = extractItem(await risk_portal_risks(params));
            const list = (d && Array.isArray(d.results)) ? d.results : [];
            setAll(list.filter((r) => visibleToMe(r, { role, email, stages })));
        } catch (e) {
            console.error('Load my risks failed', e);
            setAll([]);
            notify('Failed to load your risks', 'error');
        } finally {
            setLoading(false);
        }
    }, [role, email, stages, notify]);

    useEffect(() => { load(); }, [load]);

    const runSearch = () => { setSearch(searchText.trim().toLowerCase()); setPage(0); };

    // Apply the search filter, then paginate client-side.
    const filtered = useMemo(() => {
        if (!search) return all;
        return all.filter((r) =>
            String(r.risk_id || '').toLowerCase().includes(search) ||
            String(r.risk || '').toLowerCase().includes(search));
    }, [all, search]);

    const pageCount = Math.ceil(filtered.length / PAGE_SIZE) || 1;
    const pageItems = filtered.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

    const HEADERS = ['Risk ID', 'Description', 'Current Stage', 'State', 'Status'];

    const rows = pageItems.map((r) => {
        const stage = resolveCurrentStage(r, stages);
        const stageOwner = stageOwnerRoleFor(r, stages);
        const mine = canActOnRisk(r, { role, email, stages });
        // A closed risk has finished its lifecycle — no one is awaiting or acting.
        const isClosed = String(r.state || '').toLowerCase() === 'closed';
        return {
            data: [
                <Body as="span" color="var(--color-primary)" weight="semibold">{r.risk_id || '—'}</Body>,
                r.risk || '—',
                <Body as="span" color="var(--color-text-primary)">{stage?.name || stage?.label || '—'}</Body>,
                <StatusBadge status={r.state} label={r.state_label} />,
                isClosed ? (
                    <Body as="span" className="rp1_badge rp1_badge--neutral">Completed</Body>
                ) : mine ? (
                    <Body as="span" className="rp1_badge rp1_badge--success">Action required</Body>
                ) : (
                    <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
                        <Body as="span" className="rp1_badge rp1_badge--warning" style={{ alignSelf: 'flex-start' }}>
                            Awaiting {titleCase(stageOwner) || 'response'}
                        </Body>
                        {r.risk_owner && (
                            <Body as="span" variant={2} color="var(--color-text-muted)" style={{ fontSize: fontSize.sm }}>
                                Owner: {r.risk_owner}
                            </Body>
                        )}
                    </span>
                ),
            ],
        };
    });

    // A clicked row opens the single-risk workflow detail (in-place, so Back
    // returns to this queue). Falls back to the shell handler if provided.
    if (detailRiskId != null) {
        return (
            <RiskDetail
                riskId={detailRiskId}
                role={role}
                onBack={() => { setDetailRiskId(null); load(); }}
            />
        );
    }

    return (
        <div className="rp1_register rp1_widget">
            <div className="rp1_register_toolbar">
                <PageHeading title="My Risks" subtitle="Risks you own or that are waiting on your role" />
            </div>

            <div className="rp1_register_filters">
                <CommonSearchBar value={searchText} onChange={setSearchText} onEnter={runSearch} placeholder="Search my risks..." />
            </div>

            <div className="rp1_register_table">
                {loading && <CommonLoader overlay label="Loading your risks…" />}
                {rows.length === 0 ? (
                    <NoDataPage
                        title="No risks assigned to you"
                        hint="Risks you own — or that reach a stage your role handles — show up here."
                    />
                ) : (
                    <CommonTable
                        headers={HEADERS}
                        rows={rows}
                        noPadding
                        noScroll
                        onRowClick={(_row, i) => {
                            const id = pageItems[i]?.id;
                            if (id == null) return;
                            if (onOpenRisk) onOpenRisk(id); else setDetailRiskId(id);
                        }}
                    />
                )}
            </div>

            {pageCount > 1 && (
                <div className="rp1_register_pagination">
                    <Body as="span" variant={2} weight="medium" style={{ fontSize: fontSize.sm, color: 'var(--color-text-muted)' }}>
                        Page {page + 1} of {pageCount} ({filtered.length} total)
                    </Body>
                    <CommonPagination
                        pageCount={pageCount}
                        forcePage={page}
                        handlePageClick={({ selected }) => setPage(selected)}
                    />
                </div>
            )}

            {toastNode}
        </div>
    );
}
